/**
 * Launcher V2 — PR B follow-up U7 (R7): inline "in bucket" / "sends" counts
 * for a segmentation row, without requiring a Preview click.
 *
 * DOM-free and Prisma-free on purpose, the same reason `members-panel-scope.ts`
 * / `review-gate.ts` / `matched-members.ts` are: `bun:test` has no DOM under
 * `mini-apps/`, so any logic that lives inside a Glaze-importing `.tsx` file is
 * untestable there.
 *
 * Deliberately does NOT re-implement `matchedIdsForRungScope`'s id-keyed ladder
 * lookup or its version guard — it delegates to that exact function so this
 * module and `MembersPanel`'s Preview path can never quietly disagree about
 * which rung a ladder entry belongs to, or about when a ladder is too stale to
 * trust.
 */

import { matchedIdsForRungScope } from './members-panel-scope';
import type { PlaybooksMatchedMembersLadderResponse, PlaybookRung } from './schemas';

export interface RungInlineCounts {
  /** First-match-wins matched member count for this rung ("X in bucket"). */
  inBucket: number;
  /**
   * `inBucket` when a sequence is mapped, else `0` — v2 has no per-channel
   * "cell enabled" concept the way v1's `calculateWillSend` did (Spencer
   * confirmed this redefinition), so "sends" here means "will this rung's
   * matched members actually get a send" rather than a per-channel readiness
   * split.
   */
  sends: number;
}

/**
 * `null` — never a guessed/fallback zero — whenever
 * `matchedIdsForRungScope` can't resolve a real answer: the ladder hasn't
 * loaded, describes a different Playbook version than the one rendered, or
 * carries no entry for this rung's id at all. Callers render an em-dash in that
 * case (the same honesty rule `reviewMemberTally` follows).
 */
export function rungInlineCounts(
  ladder: PlaybooksMatchedMembersLadderResponse | undefined,
  rung: PlaybookRung,
  expectedPlaybookVersionId: string | null,
): RungInlineCounts | null {
  const ids = matchedIdsForRungScope(ladder, rung.id, expectedPlaybookVersionId);
  if (ids === null) return null;
  return {
    inBucket: ids.length,
    sends: rung.mapping !== null ? ids.length : 0,
  };
}

/**
 * U6 (R-Q1) — the rung-draft live-filter-preview count. Takes a plain
 * `matchedCount` rather than a `PlaybooksMatchedMembersLadderResponse`
 * because an unsaved draft's conditions have no ladder entry OF THEIR OWN to
 * read (they aren't the rung's saved segmentation) — the caller has already
 * done the ladder-aware work before calling this.
 *
 * W2 (fix-round2-plan.md, superseding this function's original "standalone,
 * no ladder exclusion" doc) — the caller (`QualifyStep.tsx`'s `RungRow`,
 * `MembersPanel`'s `'rung-draft'` branch) now hands in
 * `matchedIdsForRungDraftScope(...)?.length`, i.e. the raw live-filter match
 * already reduced by every ladder rung ABOVE this one's claimed members, not
 * the raw `matchMembersByFilter` count. `previewRungCounts` itself is
 * UNCHANGED — it still just turns a count into `{ inBucket, sends }` — the
 * ladder-awareness lives entirely in what count it's handed, which is what
 * makes the inline count and Members panel row agree with what Save would
 * actually produce (Codex round-2 "Will Break #2": the OLD raw count reported
 * 100 for a rung-2 draft while saving produced 10).
 *
 * `null` matchedCount (the ladder-aware id list isn't resolvable yet — either
 * query pending, or `matchedIdsForRungDraftScope` itself returned `null`)
 * stays `null` — mirrors `rungInlineCounts`'s house rule (this module's
 * header): never a guessed `0`.
 */
export function previewRungCounts(matchedCount: number | null, isMapped: boolean): RungInlineCounts | null {
  if (matchedCount === null) return null;
  return {
    inBucket: matchedCount,
    sends: isMapped ? matchedCount : 0,
  };
}
